/**
 * Permission Hook for Role-Based Access Control
 * 
 * This hook checks if the current user has permission to view/add/edit/delete
 * based on their role and assigned permissions.
 * 
 * For Admin users (role = 1): All permissions are granted
 * For Sub-Admin users: Permissions are checked against their assigned role
 */
import { useMemo } from 'react';
import { useAuth } from '../state/hooks';
import { RESOURCES } from '../constants/modules';
import type { Permission } from '../api/types/permission.types';

interface UsePermissionOptions {
  label: string;
  sideBarId?: number;
  page_permission?: 'isView' | 'isAdd' | 'isEdit' | 'isDelete';
}

interface UsePermissionResult {
  isView: boolean;
  isAdd: boolean;
  isEdit: boolean;
  isDelete: boolean;
  hasPermission: (permission: 'isView' | 'isAdd' | 'isEdit' | 'isDelete') => boolean;
}

/**
 * Get sidebar ID from label
 */
function getSideBarIdFromLabel(label: string): number | null {
  const labelToIdMap: Record<string, number> = {
    'Dashboard': RESOURCES.DASHBOARD,
    'Manage Users': RESOURCES.USERS,
    'Workout management': RESOURCES.WORKOUTS,
    'Manage Programs': RESOURCES.PROGRAMS,
    'Subscription': RESOURCES.SUBSCRIPTIONS,
    'Achievements & Badges': RESOURCES.BADGES,
    'Categories': RESOURCES.CATEGORIES,
    'Revenue Management': RESOURCES.REVENUE,
    'Discounts and Coupons': RESOURCES.COUPONS,
    'Notification Management': RESOURCES.NOTIFICATIONS,
    'Report Analytics': RESOURCES.REPORTS_ANALYTICS,
    'Manage Sub-admin': RESOURCES.SUBADMIN,
    'Community Moderation': RESOURCES.COMMUNITY,
    'Manage CMS Pages': RESOURCES.CMS,
    'System Settings': RESOURCES.SETTINGS,
  };

  return labelToIdMap[label] || null;
}

/**
 * Permission hook for checking user permissions
 */
export function usePermission(options: UsePermissionOptions): UsePermissionResult {
  const user = useAuth();

  const permissions = useMemo(() => {
    // Default: no permissions
    let isView = false;
    let isAdd = false;
    let isEdit = false;
    let isDelete = false;

    // If no user, return no permissions
    if (!user) {
      return { isView, isAdd, isEdit, isDelete };
    }

    // Check user role
    // Role can be string or number, so we need to handle both
    const userRole = user.role;
    let roleNumber = 0;
    
    if (typeof userRole === 'string') {
      roleNumber = parseInt(userRole, 10);
    } else if (typeof userRole === 'number') {
      roleNumber = userRole;
    }
    
    // Admin users (role = 1) always have full access, regardless of permissionId
    if (roleNumber === 1) {
      return {
        isView: true,
        isAdd: true,
        isEdit: true,
        isDelete: true,
      };
    }

    // Get the sidebar ID from label or use provided sideBarId
    const sideBarId = options.sideBarId || getSideBarIdFromLabel(options.label);

    if (!sideBarId) {
      // If we can't find the sidebar ID, deny access
      return { isView, isAdd, isEdit, isDelete };
    }

    // For sub-admin users, check their permissions from permissionId
    const permissionId = (user as any).permissionId;
    
    // If user has permissionId, use those permissions
    if (permissionId && permissionId.permission && Array.isArray(permissionId.permission)) {
      // Find the permission for this specific module
      const modulePermission = permissionId.permission.find(
        (perm: Permission) => {
          const permSideBarId = typeof perm.sideBarId === 'string' ? parseInt(perm.sideBarId, 10) : perm.sideBarId;
          return permSideBarId === sideBarId;
        }
      );

      if (modulePermission) {
        isView = modulePermission.isView || false;
        isAdd = modulePermission.isAdd || false;
        isEdit = modulePermission.isEdit || false;
        isDelete = modulePermission.isDelete || false;
      }
      
      return { isView, isAdd, isEdit, isDelete };
    }

    // Fallback: Try other permission structures for backward compatibility
    const userPermissions = (user as any).permissionDetails?.permission || 
                           (user as any).permissionDetails ||
                           (user as any).permission || 
                           [];

    // Ensure it's an array
    const permissionsArray = Array.isArray(userPermissions) ? userPermissions : [];

    // Find the permission for this specific module
    const modulePermission = permissionsArray.find(
      (perm: Permission) => {
        const permSideBarId = typeof perm.sideBarId === 'string' ? parseInt(perm.sideBarId, 10) : perm.sideBarId;
        return permSideBarId === sideBarId;
      }
    );

    if (modulePermission) {
      isView = modulePermission.isView || false;
      isAdd = modulePermission.isAdd || false;
      isEdit = modulePermission.isEdit || false;
      isDelete = modulePermission.isDelete || false;
    }

    return { isView, isAdd, isEdit, isDelete };
  }, [user, options.label, options.sideBarId]);

  const hasPermission = (permission: 'isView' | 'isAdd' | 'isEdit' | 'isDelete'): boolean => {
    return permissions[permission];
  };

  return {
    ...permissions,
    hasPermission,
  };
}

/**
 * Check if user can view a specific module by label
 * Simplified version for conditional rendering
 */
export function useCanView(label: string): boolean {
  const { isView } = usePermission({ label });
  return isView;
}

